import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges, ViewChild
} from '@angular/core';
import {Observable, Subscription} from "rxjs";
import * as mime from "mime";
import {KnockoutFieldModel} from "./knockout-field.model";
import {FormioCustomComponent} from "@formio/angular";

/**
 * TODO does not support multiple reference fields, but does support onetomany renderer for reference fields.
 * TODO it does not support default values for multiple reference fields, but could be modified along the lines
 * TODO the lines of CatalogFieldComponent to do this.
 *
 * This component can render either using formio or knockout fields. The formio ones are used by default but the
 * knockout ones are used when the datatype is reference, knockout-string or knockout-choice.
 *
 * This is similar to CatalogFieldComponent and the two could probably be unified at some point.
 */
@Component({
  selector: 'sb-knockout-field',
  templateUrl: './knockout-field.component.html',
  styleUrls: ['./knockout-field.component.scss']
})
export class KnockoutFieldComponent implements FormioCustomComponent<string>, OnInit, AfterViewInit, OnDestroy {

  @ViewChild('target')
  target: ElementRef<HTMLElement>;

  @Input()
  disabled: boolean;

  // If serializeValue is true then we send and receive a stringify version to formio while
  // maintaining the non-stringify version for the knockout renderer.
  @Input()
  get value() {
    return this.serializeValue ? (this._value != null ? JSON.stringify(this._value) : this._value) : this._value;
  }
  set value(value: any) {
    this._value = this.serializeValue ? (value != null ? JSON.parse(value) : value) : value;
  }
  _value: string | number | boolean;

  @Input()
  serializeValue: boolean = false;

  @Input()
  preview: boolean;

  @Input()
  field: KnockoutFieldModel;

  @Input()
  events: Observable<any>;

  @Output()
  valueChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  fieldRenderInfo: EventEmitter<any> = new EventEmitter<any>();

  private eventsSubscription: Subscription;

  firstSettingOfChoice: boolean = true;

  firstSettingOfHtml: boolean = true;

  koFieldModel: any = null;

  knockoutTemplateRendered: boolean = false;

  // Note that an attachment field is a reference field with referenceTable of 'Attachment'
  private datatypeToCreatorFn: {
    [key: string]: (model: KnockoutFieldComponent, field: KnockoutFieldModel) => any
  } = {
    choice: this.createChoiceField,
    text: this.createScalarField,
    html: this.createScalarField,
    boolean: this.createScalarField,
    acknowledgement: this.createScalarField,
    integer: this.createScalarField,
    string: this.createScalarField,
    date_time: this.createScalarField,
    date: this.createScalarField,
    decimal: this.createScalarField,
    duration: this.createScalarField,
    reference: this.createReferenceField,
    many_to_many: this.createReferenceField,
    'knockout-string': this.createScalarField,
    'knockout-choice': this.createChoiceField,
    money: this.createScalarField,
  };

  // Note that the default renderer for reference is 'attachment' if the referenceTable is 'Attachment'
  private datatypeToDefaultRenderer: {
    [key: string]: string
  } = {
    choice: "choice",
    text: "textarea",
    html: "html",
    boolean: "checkbox",
    acknowledgement: "checkbox",
    integer: "textfield",
    string: "textfield",
    date_time: "datetime",
    date: "date",
    decimal: "textfield",
    duration: 'duration',
    reference: "onetoone",
    many_to_many: "onetomany",
    money: "accounting",
    'knockout-string': 'textfield',
    'knockout-choice': 'choice',
  };

  constructor() {
  }

  ngOnInit() {
    if (this.events) {
      this.eventsSubscription = this.events.subscribe((data) => this.eventSubscription(data));
    }
  }

  ngOnDestroy() {
    (this.eventsSubscription) && this.eventsSubscription.unsubscribe();
  }

  async ngAfterViewInit() {
    // For some reason Chrome and FireFox sends changes in two batches with the second batch having just the value set.
    // Safari sends the changes in one batch. We wait 100ms for Angular to get all the change detection out of the way
    // before rendering the knockout template.
    setTimeout(() => {
      this.renderKnockoutTemplate();
    }, 100);
  }

  // The OnChanges lifecycle hook with the setTimeout() breaks too much stuff, especially Data Import wizards. It was
  // put in for SER-919 and removed for SER-1010.
  //
  // ngOnChanges(changes: SimpleChanges) {
  //   // For some reason Chrome and FireFox sends changes in two batches with the second batch having just the value set.
  //   // Safari sends the changes in one batch. We wait 100ms for Angular to get all the change detection out of the way
  //   // before rendering the knockout template.
  //   setTimeout(() => {
  //     this.renderKnockoutTemplate();
  //   }, 100);
  // }

  private handleFieldChange(field: KnockoutFieldModel) {
    // This value is coming from Knockout
    this._value = field.data.answer.value;

    // This is going to Formio so it has to match value
    this.valueChange.emit(this.value);

    // This is not going to formio so it has to be the real value
    field.valueChangeCallback && field.valueChangeCallback(this._value, field.data.answer.displayValue );
  }

  private eventSubscription(data) {
    switch (data) {
      case "validate":
        if (this.koFieldModel != null && this.koFieldModel.validateField) {
          this.koFieldModel.validateField();
        }
        break;
      case "clear":
        if (this.koFieldModel != null && this.koFieldModel.value) {
          this.koFieldModel.value(null);
        }
        break;
    }
  }

  private generateVirtualTableName(field: KnockoutFieldModel): string {
    return field.table;
  }

  private generateI18nKey(field: KnockoutFieldModel): string {
    return field.i18nKey;
  }

  private async renderKnockoutTemplate() {
    if (this.field && !this.knockoutTemplateRendered) {

      if (this.target) {
        const element = this.target.nativeElement;

        const creatorFn = this.datatypeToCreatorFn[this.field.datatype];

        if (creatorFn) {

          // If we have an injected value it is from the saved dashboard definition, possibly stringified.
          if (this._value) {
            this.field.data.answer = {value: this._value, displayValue: ""}; // TODO: the display value doesn't seem to be needed.
          }

          const data = await creatorFn(this, this.field);

          // Add the form display type, as Knockout uses this info for styles
          data.formDisplayType = "form";

          // Add a callback to the binding context that nested templates can use to pass info back up the tree,
          // which can be passed on to containing components.
          data.fieldRenderInfoCallback = (data: any) => {
            this.fieldRenderInfo.emit(data);
          };

          // Some field creators want to know when the field has been created so they can subscribe to the value
          // e.g. PortalListComponent
          if (this.field.fieldCreationCallback) {
            this.field.fieldCreationCallback(data);
          }

          (window as any).KO.cleanNode(element);
          (window as any).KO.renderTemplate("nuclear-field-template", data, {}, element, "replaceNode");
          this.koFieldModel = data.field;
        } else {
          console.error(`No creator function for field of type ${this.field.datatype}`);
        }
        this.knockoutTemplateRendered = true;
      }
    }
  }

  private async createScalarField(model: KnockoutFieldComponent, field: KnockoutFieldModel) {
    let fieldSet = false;

    const fieldName = field.name;
    const fieldType = field.datatype;

    const properties: any = {
      fieldName: fieldName,
      table: field.table,
      fieldType: fieldType,
      renderer: field.renderer || model.datatypeToDefaultRenderer[fieldType],
      rendererProperties: field.rendererProperties || {},
      required: field.mandatory,
      readOnly: field.readonly,
      viewReadOnly: field.readonly,
      i18nKey: model.generateI18nKey(field),
      tooltipI18nKey: field.tooltip,
    };

    // For html fields we have to observe fewer events so we don't send so many requests to the server.
    if (fieldType == "html") {
      properties.rendererProperties["tinymce.events"] = "focus blur dirty";
    }

    let defaultValueSource = field.data.defaultValueSource;

    if (defaultValueSource == "question" && field.data.defaultValue) {
      properties.initialValue = field.data.defaultValue.value;
      fieldSet = true;
    } else if ((defaultValueSource == "function" || defaultValueSource == "script" || defaultValueSource == "nlu") && field.data.defaultValue && !field.data.answer) {
      properties.initialValue = field.data.defaultValue.value;
      fieldSet = true;
    } else if (field.data.answer) {
      properties.initialValue = field.data.answer.value;
      fieldSet = true;
    }

    const bindingContext = (window as any).KO_FIELD_DEFINITION_BRIDGE.createStandaloneFieldDefinition(model, properties);

    bindingContext["data"][fieldName].value.subscribe((value) => {
      // The tinyMCE html editor will always set the value to empty string the first time.
      if (value == "" && model.firstSettingOfHtml == true && fieldType == "html") {
        model.firstSettingOfHtml = false;
        return;
      }

      if (!model.field.data.answer) {
        model.field.data.answer = {};
      }
      model.field.data.answer.value = value;

      model.handleFieldChange(model.field);
    });

    // Special case for queryBuilder which is a string with a condition renderer. The interactions between the knockout
    // and Angular/formio sides are a bit gnarly. We might be able to get away with only one of 'portal.onQueryUpdateFn'
    // and 'portal.onApplyFilter'
    if (properties.fieldType === "string" && properties.renderer === "condition") {

      properties.rendererProperties['portal.onQueryUpdateFn'] = (queryObject) => {
        if (!model.field.data.answer) {
          model.field.data.answer = {};
        }
        model.field.data.answer.value = JSON.stringify(queryObject);
        model.handleFieldChange(model.field);
      }

      properties.rendererProperties['portal.onApplyFilter'] = (queryObject) => {
        if (!model.field.data.answer) {
          model.field.data.answer = {};
        }
        model.field.data.answer.value = JSON.stringify(queryObject);
        model.handleFieldChange(model.field);
      }
    }

    // Force the update if a default value is supplied.
    if (fieldSet) {
      // bindingContext["data"][fieldName].value(bindingContext["data"][fieldName].value());
      // Doesn't work for scalar fields as KO ignores setting the same value that it already has.
      if (!model.field.data.answer) {
        model.field.data.answer = {};
      }
      model.field.data.answer.value = bindingContext["data"][fieldName].value();
      model.handleFieldChange(model.field);
    }

    let result: any = {
      getAspect() {
        return "SelfService";
      },
      "$root": {
        formDisplayType: 'form',
      },
      fieldDefinition: bindingContext["fieldDefinition"],
      field: bindingContext["data"][fieldName],
      record: bindingContext["record"],
      data: {
        isNewRecord: () => {
          return true;
        }
      },
    };

    // This is a misuse of the UIEvent executor for knockout fields on Angular pages to allow detection of
    // keyup and onClick events.
    if (field.keyupEventCallback || field.onclickEventCallback) {
      const eventExecutor: any = {};
      if (field.keyupEventCallback) {
        eventExecutor.keyup = field.keyupEventCallback;
      }
      if (field.onclickEventCallback) {
        eventExecutor.onClick = field.onclickEventCallback;
      }
      result.getEventExecutor = function() { return eventExecutor; }
    }

    return result;
  }

  private async createReferenceField(model: KnockoutFieldComponent, field: KnockoutFieldModel) {
    let fieldSet = false;

    const fieldName = field.name;
    const fieldType = field.datatype;
    const tableName = model.generateVirtualTableName(field);

    const isMulti = fieldType == 'many_to_many';

    const properties: any = {
      fieldName: fieldName,
      table: tableName,
      renderer: field.referenceTable === "Attachment" ? "attachment" : field.renderer || model.datatypeToDefaultRenderer[fieldType],
      required: field.mandatory,
      readOnly: field.readonly,
      viewReadOnly: field.readonly,
      i18nKey: model.generateI18nKey(field),
      tooltipI18nKey: field.tooltip,
      rendererProperties: {
        useReferenceQualifiers: true,
        applyAllowReadFromParent: false,
        enablePreview: field.enablePreview !== undefined ? field.enablePreview : true,
          ...(field.rendererProperties || {})
      }
    };

    if (field.data.defaultValueSource == "question" && field.data.defaultValue) {
      fieldSet = true;
      properties.initialValue = {
        id: field.data.defaultValue.value,
        value: {
          displayValue: field.data.defaultValue.displayValue,
          readOnly: field.readonly,
          required: field.mandatory,
          value: field.data.defaultValue.value
        }
      }
    } else if (field.data.defaultValueSource == "function" && field.data.defaultValue && !field.data.answer) {
      fieldSet = true;
      properties.initialValue = {
        id: field.data.defaultValue.value,
        value: {
          displayValue: field.data.defaultValue.displayValue,
          readOnly: field.readonly,
          required: field.mandatory,
          value: field.data.defaultValue.value
        }
      }
    } else if (field.data.defaultValueSource == "script" && field.data.defaultValue && !field.data.answer) {
      fieldSet = true;
      properties.initialValue = {
        id: field.data.defaultValue.value,
        value: {
          displayValue: field.data.defaultValue.displayValue,
          readOnly: field.readonly,
          required: field.mandatory,
          value: field.data.defaultValue.value
        }
      }
    } else if (field.data.answer) {
      fieldSet = true;
      properties.initialValue = {
        id: field.data.answer.value,
        value: {
          displayValue: field.data.answer.displayValue,
          readOnly: field.readonly,
          required: field.mandatory,
          value: field.data.answer.value
        }
      }
    }

    if (field.referenceTable === "Attachment") {
      properties.relationType = "reference";

      if (properties.initialValue) {
        properties.initialValue.value.additionalValues =
          {MimeType: mime.getType(properties.initialValue.value.displayValue)};
      }
    }

    const bindingContext = (window as any).KO_FIELD_DEFINITION_BRIDGE.createStandaloneReferenceFieldDefinition(model, properties);

    bindingContext["data"][fieldName].value.subscribe((value) => {
      // The value will be null if the reference field is cleared. It will be [] if a multi reference and cleared.
      if (field.referenceTable === "Attachment") {
        // The value might be an array of attachment ids
        if (!model.field.data.answer) {
          model.field.data.answer = {};
        }

        model.field.data.answer.value = Array.isArray(value) ? value.join(',') : value;
      } else {
        // The value is an object containing the reference id
        if (!model.field.data.answer) {
          model.field.data.answer = {};
        }
        if (isMulti) {
          model.field.data.answer.value = !value ? null : (Array.isArray(value) ? value.map((v => v.id)).join(',') : value);
        } else {
          model.field.data.answer.value = value ? value.id : null;
        }
      }
      model.handleFieldChange(model.field);
    });

    // The reference field doesn't update the value if you supply a default value so we force it.
    if (field.data.defaultValue) {
      bindingContext["data"][fieldName].value(bindingContext["data"][fieldName].value());
    }

    // The attachment renderer needs a record
    let record = bindingContext["record"];
    if (!record && field.referenceTable === "Attachment") {
      record = {
        TableClass: {
          value: () => {
            return field.table;
          }
        },
        id: () => {
          return field.requestId;
        }
      }
    }

    let result: any = {
      getAspect() {
        return "SelfService";
      },
      "$root": {
        formDisplayType: 'form',
      },
      fieldDefinition: bindingContext["fieldDefinition"],
      field: bindingContext["data"][fieldName],
      record: record,
      data: {
        isNewRecord: () => {
          return true;
        },
      },
    };

    // Used by the attachment field to get details about the uploaded file
    if (field.onSuccessCallback) {
      result.onSuccess = field.onSuccessCallback;
    }

    return result;
  }

  private async createChoiceField(model: KnockoutFieldComponent, field: KnockoutFieldModel) {

    let fieldSet = false;

    const fieldName = field.name;
    const fieldType = field.datatype;

    const properties: any = {
      fieldName: fieldName,
      table: field.table,
      fieldType: fieldType,
      renderer: field.renderer || model.datatypeToDefaultRenderer[fieldType],
      rendererProperties: field.rendererProperties || {},
      availableValues: field.availableValues,
      required: field.mandatory,
      readOnly: field.readonly,
      viewReadOnly: field.readonly,
      i18nKey: model.generateI18nKey(field),
      tooltipI18nKey: field.tooltip,
    };

    if (field.data.defaultValueSource == "question" && field.data.defaultValue) {
      properties.initialValue = field.data.defaultValue.value;
      fieldSet = true;
    } else if (field.data.defaultValueSource == "function" && field.data.defaultValue && !field.data.answer) {
      properties.initialValue = field.data.defaultValue.value;
      fieldSet = true;
    } else if (field.data.defaultValueSource == "script" && field.data.defaultValue && !field.data.answer) {
      properties.initialValue = field.data.defaultValue.value;
      fieldSet = true;
    } else if (field.data.answer) {
      properties.initialValue = field.data.answer.value;
      fieldSet = true;
    }

    let bindingContext = (window as any).KO_FIELD_DEFINITION_BRIDGE.createStandaloneFieldDefinition(model, properties);

    let koField = bindingContext["data"][fieldName];

    koField.value.subscribe((value) => {
      // The select2 binding will always set null on the field if there is no initial value and also
      // when the field is cleared. We have to distinguish the two cases.
      if ((value == null && model.firstSettingOfChoice == false) || value != null) {
        if (!model.field.data.answer) {
          model.field.data.answer = {};
        }
        model.field.data.answer.value = value;
        model.handleFieldChange(model.field);
      }
      model.firstSettingOfChoice = false;
    });

    // Force the update if a default value is supplied.
    if (fieldSet) {
      koField.value(koField.value());
    }

    let result: any = {
      getAspect() {
        return "SelfService";
      },
      "$root": {
        formDisplayType: 'form',
      },
      fieldDefinition: bindingContext["fieldDefinition"],
      field: koField,
      record: bindingContext["record"],
      data: {
        isNewRecord: () => {
          return true;
        }
      }
    };

    return result;
  }
}
